// Thin client for the `chidesk-proxy` edge function plus the deposit
// preview / payment-intent RPCs. The booking flow is:
//
//   1. previewChideskDeposit(items)             — quote-time display
//   2. (if total > 0) createChideskDepositPaymentIntent — payments row
//   3. (if total > 0) savePendingChideskBooking + initializePaystackCheckout
//      — stash the cart so the resume handler can read it back, then
//      redirect to the Paystack hosted page
//   4. on return (URL has ?vt_chidesk_ref=…) → poll
//      getChideskPaymentStatus until status='success', then call
//      bookChideskCart({ paymentReference }) — chidesk-proxy verifies
//      the reference, books, and stamps it on every
//      chidesk_appointment so chidesk-poll's auto-refund flow can find
//      it on cancellation.

import { supabase } from "../config";
import {
  labelFromWallMinutes,
  normMinute,
  tzOffsetMinutes,
  wallMinutesOf,
} from "./chidesk-availability";

export interface ChideskGuestContact {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
}

export interface ChideskStaffCandidate {
  guid: string;
  staff_row_id: string | null;
  name: string | null;
}

export interface ChideskBookItem {
  treatment_row_id: string;
  chidesk_treatment_guid: string;
  // Set only when the guest picked a SPECIFIC therapist. When null the server
  // assigns fairly (least-loaded) from `chidesk_staff_candidates`.
  chidesk_staff_guid?: string | null;
  chidesk_staff_row_id?: string | null;
  // Therapists free at the chosen slot — the pool the server circulates a fair
  // assignment across for an "Any available" booking (just the picked one for a
  // specific choice). Empty/undefined → let ChiDesk assign.
  chidesk_staff_candidates?: ChideskStaffCandidate[];
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  amount_cents: number;
  // Per-treatment deposit taken now (cents). Lets the proxy note "deposit paid,
  // X%, R amount" on the ChiDesk appointment. 0 / undefined → no deposit.
  deposit_cents?: number;
  currency: string;
  treatment_name: string;
  // Null / undefined → billed to the primary booker.
  for?: ChideskGuestContact | null;
}

export interface ChideskBookResult {
  ok: boolean;
  booked: number;
  failed: number;
  results: Array<{
    index: number;
    ok: boolean;
    treatment_name: string;
    chidesk_appointment_id?: string;
    /**
     * Human-friendly appointment code (e.g. LAB-260516-K3PTRN)
     * surfaced by the chidesk-proxy edge function. Populated by the
     * chidesk_appointments BEFORE INSERT trigger from
     * 20260516000006_chidesk_appointment_reference.sql. Optional
     * because deployments without the migration applied yet return
     * undefined.
     */
    reference?: string | null;
    error?: string;
  }>;
}

export type ChideskBookResponse =
  | ChideskBookResult
  | { ok: false; error: string; detail?: string };

// The proxy's pre-booking failures (bad/missing ChiDesk credentials, a 401
// from ChiDesk, a reused deposit reference) come back as non-2xx with a
// structured { error, detail } body — but supabase-js collapses that to a
// generic "non-2xx status code" message. Map the known codes to something a
// guest (and our console) can actually act on.
const CHIDESK_BOOK_ERRORS: Record<string, string> = {
  credentials_missing:
    "the spa booking system isn't fully connected yet — the venue needs to finish the ChiDesk setup",
  site_id_not_configured:
    "the spa booking system isn't fully connected yet — the venue needs to finish the ChiDesk setup",
  integration_inactive: "the spa booking system is switched off for this venue",
  integration_not_found: "the spa booking system isn't set up for this venue",
  client_create_failed: "the spa booking system rejected the request",
  payment_reference_already_used:
    "this deposit was already used to book — check your email for the confirmation",
  payment_not_settled: "the deposit hasn't settled yet — refresh in a moment",
  payment_reference_not_found: "we couldn't match your deposit payment",
};

function friendlyChideskError(code: string, detail?: string): string {
  const base = CHIDESK_BOOK_ERRORS[code];
  if (base) return detail ? `${base} (${code})` : base;
  // Unknown code — surface it raw (with any detail) so it's diagnosable.
  return detail ? `${code}: ${detail}` : code;
}

export async function bookChideskCart(input: {
  tenantIntegrationId: string;
  cartToken: string;
  primary: ChideskGuestContact;
  items: ChideskBookItem[];
  // The Paystack reference of a successful deposit charge, if a deposit
  // was required. Stamped on every chidesk_appointment the proxy
  // creates.
  paymentReference?: string;
  // Post-payment partial-booking resolution: re-book JUST the failed leg(s) at
  // the guest's newly-chosen time against the deposit ALREADY paid. Tells the
  // proxy to skip the "reference already used" guard (appointments exist for the
  // legs that did book) and rely on its per-leg idempotency instead. No
  // re-charge — the deposit was taken at the original checkout.
  resolvePending?: boolean;
}): Promise<ChideskBookResponse> {
  try {
    const { data, error } = await supabase.functions.invoke("chidesk-proxy", {
      body: {
        action: "book",
        tenant_integration_id: input.tenantIntegrationId,
        cart_token: input.cartToken,
        source: "widget",
        primary: input.primary,
        items: input.items,
        payment_reference: input.paymentReference,
        resolve_pending: input.resolvePending ?? false,
      },
    });
    if (error) {
      // On a non-2xx the real reason is in the response body — dig it out.
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        try {
          const b = (await ctx.json()) as {
            error?: string;
            detail?: string;
          } | null;
          if (b?.error) {
            // Keep the raw code+detail in the console for diagnosis.
            console.error("[chidesk-proxy] book failed", b);
            return {
              ok: false,
              error: friendlyChideskError(b.error, b.detail),
              detail: b.detail,
            };
          }
        } catch {
          /* body wasn't JSON — fall through to the generic message */
        }
      }
      return { ok: false, error: error.message || "network_error" };
    }
    if (!data || typeof data !== "object") {
      return { ok: false, error: "empty_response" };
    }
    return data as ChideskBookResponse;
  } catch (e) {
    return { ok: false, error: (e as Error).message || "network_error" };
  }
}

// Post-payment partial-booking resolution, CANCEL branch: the guest chose to
// drop the treatment(s) that couldn't be booked instead of re-timing them.
// Refund the summed deposit for those legs against the original charge (the
// booked legs are untouched — a PARTIAL refund). Sum the cancelled legs into
// ONE call so the proxy's idempotent list-first guard stays correct.
export async function refundPendingLegs(input: {
  tenantIntegrationId: string;
  paymentReference: string;
  depositCents: number;
  /** Possession proof — the proxy checks it against payments.cart_token. */
  cartToken: string;
}): Promise<{ ok: boolean; refunded?: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke("chidesk-proxy", {
      body: {
        action: "refund_pending_leg",
        tenant_integration_id: input.tenantIntegrationId,
        payment_reference: input.paymentReference,
        deposit_cents: input.depositCents,
        cart_token: input.cartToken,
      },
    });
    if (error) {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        try {
          const b = (await ctx.json()) as { error?: string; detail?: string } | null;
          if (b?.error) {
            console.error("[chidesk-proxy] refund_pending_leg failed", b);
            return { ok: false, error: b.detail ? `${b.error}: ${b.detail}` : b.error };
          }
        } catch {
          /* not JSON */
        }
      }
      return { ok: false, error: error.message || "network_error" };
    }
    const d = (data ?? {}) as { ok?: boolean; refunded?: boolean };
    return { ok: d.ok !== false, refunded: d.refunded };
  } catch (e) {
    return { ok: false, error: (e as Error).message || "network_error" };
  }
}

// ─── Pre-payment availability re-check ─────────────────────────────────
// Called at checkout BEFORE taking the deposit (chidesk-proxy
// `recheck_availability`), so a slot grabbed between pick-time and checkout is
// caught while the guest can still choose another therapist/time or remove it —
// no deposit charged, no refund. FAILS OPEN on any transport error so a proxy
// blip never blocks checkout; the server-side finalize guard is the backstop.

export interface ChideskRecheckResult {
  index: number;
  available: boolean;
  reason: string;
  treatment_name: string;
}

export async function recheckChideskAvailability(input: {
  tenantIntegrationId: string;
  items: ChideskBookItem[];
}): Promise<{ ok: boolean; results: ChideskRecheckResult[] }> {
  const allAvailable = {
    ok: true,
    results: input.items.map((it, index) => ({
      index,
      available: true,
      reason: "",
      treatment_name: it.treatment_name,
    })),
  };
  try {
    const { data, error } = await supabase.functions.invoke("chidesk-proxy", {
      body: {
        action: "recheck_availability",
        tenant_integration_id: input.tenantIntegrationId,
        items: input.items,
      },
    });
    if (error) return allAvailable; // fail open
    const res = data as { ok?: boolean; results?: ChideskRecheckResult[] } | null;
    if (!res?.ok || !Array.isArray(res.results)) return allAvailable;
    return { ok: true, results: res.results };
  } catch {
    return allAvailable; // fail open
  }
}

// ─── Yield-management booking config ──────────────────────────────────
// Tenant's fixed allowed start times (a grid) + turnaround gap. Read via the
// dedicated get_chidesk_booking_config RPC (NOT get_widget_config). Empty grid /
// 0 turnaround → no restriction (offer every available time).

export interface ChideskBookingConfig {
  bookingGrid: number[]; // allowed minutes-of-day (TENANT-LOCAL); empty = no grid
  turnaroundMinutes: number;
  // Tenant UTC-offset (minutes) for converting ChiDesk's real-UTC slot times to
  // tenant-local display/semantics. SAST = 120 (also the fallback when the RPC
  // predates the timezone field).
  tzOffsetMinutes: number;
}

// "09:00,10:15" → [540, 615] minutes-of-day (skips unparseable entries).
function parseBookingTimes(csv: string): number[] {
  const out: number[] = [];
  for (const part of csv.split(",")) {
    const m = /(\d{1,2}):(\d{2})/.exec(part.trim());
    if (!m) continue;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (Number.isFinite(h) && Number.isFinite(min) && h < 24 && min < 60) {
      out.push(h * 60 + min);
    }
  }
  return out;
}

export async function fetchChideskBookingConfig(
  integrationId: string,
): Promise<ChideskBookingConfig> {
  try {
    const { data, error } = await supabase.rpc("get_chidesk_booking_config", {
      p_integration_id: integrationId,
    });
    if (error || !data) return { bookingGrid: [], turnaroundMinutes: 0, tzOffsetMinutes: 120 };
    const raw = data as {
      booking_times?: string;
      turnaround_minutes?: number;
      timezone?: string | null;
    };
    return {
      bookingGrid: parseBookingTimes(raw.booking_times ?? ""),
      turnaroundMinutes: Number(raw.turnaround_minutes ?? 0) || 0,
      tzOffsetMinutes: tzOffsetMinutes(raw.timezone || "Africa/Johannesburg"),
    };
  } catch {
    return { bookingGrid: [], turnaroundMinutes: 0, tzOffsetMinutes: 120 };
  }
}

// ─── Deposit preview ──────────────────────────────────────────────────
// Read-only — used by ChideskPanel to surface the deposit total in the
// cart before checkout. Pass the planned cart as `[{ treatment_row_id,
// qty }]`. Returns total + per-treatment breakdown.

export interface ChideskDepositPreview {
  ok: true;
  currency: string;
  total_cents: number;
  items: Array<{
    treatment_row_id: string;
    name: string;
    qty: number;
    deposit_cents_each: number;
    deposit_cents_total: number;
  }>;
}

export type ChideskDepositPreviewResult =
  | ChideskDepositPreview
  | { ok: false; reason: string };

export async function previewChideskDeposit(
  items: Array<{ treatment_row_id: string; qty?: number }>,
): Promise<ChideskDepositPreviewResult> {
  if (items.length === 0) {
    return { ok: true, currency: "ZAR", total_cents: 0, items: [] };
  }
  const { data, error } = await supabase.rpc("preview_chidesk_cart_deposit", {
    p_items: items,
  });
  if (error) return { ok: false, reason: error.message };
  return data as ChideskDepositPreviewResult;
}

// ─── Treatment categories (ChiDesk Category → widget grouping) ─────────────
// Powers the faceted spa menu's grouping by TREATMENT TYPE. The RPC returns the
// integration's exposed + active treatments tagged with their ChiDesk category
// label and calendar colour. The returned `id` is the WIDGET treatment row id —
// it matches chidesk.treatments[].id, so the panel keys its groups off it.
//
// `category` may be null (→ the panel buckets those into "Other treatments").
// `calendar_colour` is optional flavour only. Fails SOFT to an empty map: on any
// error every treatment falls into "Other treatments" and stays fully bookable.

export interface ChideskTreatmentCategory {
  category: string | null;
  colour: string | null;
}

export async function fetchChideskTreatmentCategories(
  integrationId: string,
): Promise<Map<string, ChideskTreatmentCategory>> {
  const map = new Map<string, ChideskTreatmentCategory>();
  const { data, error } = await supabase.rpc("get_chidesk_treatment_categories", {
    p_tenant_integration_id: integrationId,
  });
  if (error || !Array.isArray(data)) return map;
  for (const row of data as Array<{
    id?: unknown;
    category?: unknown;
    calendar_colour?: unknown;
  }>) {
    if (!row || typeof row.id !== "string" || row.id.length === 0) continue;
    const category =
      typeof row.category === "string" && row.category.trim().length > 0
        ? row.category.trim()
        : null;
    const colour =
      typeof row.calendar_colour === "string" && row.calendar_colour.trim().length > 0
        ? row.calendar_colour.trim()
        : null;
    map.set(row.id, { category, colour });
  }
  return map;
}

// ─── Live availability — rich day fetch (faceted spa flow) ─────────────────
// The faceted booking UI queries a treatment's WHOLE day of slots once, with
// staff=null ("any"), and does all therapist/time filtering client-side. That
// makes the cache monotonic (a (day, treatment)'s slots never depend on the
// selection) and keeps us to exactly one ChiDesk call per treatment per day.
//
// A slot's `start` is the proxy's `starts_at` — ChiDesk's EXACT "Start" string
// with a FAKE "Z" (wall clock is local). It is echoed VERBATIM into cart
// metadata on booking and is NEVER passed through new Date() for display; all
// display uses `label` / `minuteOfDay`, read literally by regex.

export interface ChideskSlot {
  start: string; // proxy slot.starts_at (fake-Z EXACT Start) — booked verbatim
  label: string; // display "09:00 AM"
  minuteOfDay: number; // wall-clock minute-of-day
  employeeIds: string[]; // free therapist GUIDs at that slot
}

export type ChideskDaySlots =
  | { ok: true; slots: ChideskSlot[]; byMinute: Map<number, ChideskSlot> }
  | { ok: false; error: string };

// Raw-field fallbacks — the in-repo proxy emits only { starts_at, raw }; a
// newer proxy may add start_time / employee_ids. We parse either shape so the
// widget keeps working against whichever version is deployed.
function rawStr(raw: unknown, key: string): string | null {
  if (!raw || typeof raw !== "object") return null;
  const v = (raw as Record<string, unknown>)[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}
function pickRawString(raw: unknown, keys: string[]): string | null {
  for (const k of keys) {
    const v = rawStr(raw, k);
    if (v) return v;
  }
  return null;
}
function resolveEmployeeIds(s: {
  employee_ids?: string[] | null;
  raw?: unknown;
}): string[] {
  if (Array.isArray(s.employee_ids)) {
    return s.employee_ids.filter((x): x is string => typeof x === "string" && x.length > 0);
  }
  const raw = s.raw as Record<string, unknown> | undefined;
  if (raw) {
    if (Array.isArray(raw.EmployeeIds)) {
      return (raw.EmployeeIds as unknown[]).filter(
        (x): x is string => typeof x === "string" && x.length > 0,
      );
    }
    if (Array.isArray(raw.Employees)) {
      return (raw.Employees as unknown[])
        .map((e) => {
          if (!e || typeof e !== "object") return null;
          const o = e as Record<string, unknown>;
          const id = o.EmployeeId ?? o.Id ?? o.id;
          return typeof id === "string" && id.length > 0 ? id : null;
        })
        .filter((x): x is string => x != null);
    }
  }
  return [];
}

// Shared rich-slot mapping — turns a proxy `slots[]` payload (single or a batch
// item's) into the widget's ChideskDaySlots. Kept in ONE place so the single and
// batch fetch paths map starts_at / start_time / employee_ids identically.
type RawSlot = {
  starts_at?: string | null;
  start_time?: string | null;
  employee_ids?: string[] | null;
  raw?: unknown;
};
// TZ CHOKE POINT: ChiDesk `Start` strings are REAL UTC (see chidesk-availability
// header). minuteOfDay/label here are converted to TENANT-LOCAL minutes, so
// every downstream consumer (chips, yield grid, open-hours, time-of-day filter,
// chain cursor, cart labels) is already in the tenant's wall clock. `start`
// stays the verbatim UTC string — booking echoes it unchanged. ChiDesk's own
// start_time label ("06:00 AM") is UTC-based and therefore IGNORED for minutes.
function buildDaySlots(
  rawSlots: RawSlot[] | null | undefined,
  tzOffsetMin: number,
): ChideskDaySlots {
  const slots: ChideskSlot[] = [];
  const byMinute = new Map<number, ChideskSlot>();
  for (const s of rawSlots ?? []) {
    const start = s.starts_at ?? pickRawString(s.raw, ["Start", "StartAt", "StartsAt"]);
    if (!start) continue; // no resolvable start — drop it
    const minuteOfDay = normMinute(wallMinutesOf(start) + tzOffsetMin);
    const slot: ChideskSlot = {
      start,
      label: labelFromWallMinutes(minuteOfDay),
      minuteOfDay,
      employeeIds: resolveEmployeeIds(s),
    };
    slots.push(slot);
    if (!byMinute.has(minuteOfDay)) byMinute.set(minuteOfDay, slot);
  }
  return { ok: true, slots, byMinute };
}

// Fetch a treatment's whole day of slots for ONE therapist (or "any" when
// staffGuid is null/omitted). The on-select LIVE union flow calls this once per
// bookable therapist and merges the results client-side (see
// useChideskLiveAvailability); the specific-therapist path calls it once.
//   ok:true + slots:[]  → DEFINITIVELY no availability that day (for that staff)
//   ok:false            → UNKNOWN (invoke error / !d.ok) → caller FAILS OPEN
// The noon-Z anchor is rollover-safe: the proxy derives Day via toChideskDate →
// getUTC*, so `${day}T12:00:00Z` maps to exactly `day`.
export async function fetchChideskDaySlots(input: {
  tenantIntegrationId: string;
  treatmentGuid: string;
  day: string; // 'YYYY-MM-DD'
  staffGuid?: string | null; // null/omitted = "any therapist"
  // Tenant UTC-offset for the ChiDesk-UTC → tenant-local conversion.
  // Default = SAST (+120); pass the value from fetchChideskBookingConfig.
  tzOffsetMinutes?: number;
}): Promise<ChideskDaySlots> {
  const { data, error } = await supabase.functions.invoke("chidesk-proxy", {
    body: {
      action: "available_times",
      tenant_integration_id: input.tenantIntegrationId,
      chidesk_treatment_guid: input.treatmentGuid,
      chidesk_staff_guid: input.staffGuid ?? null,
      starts_at: `${input.day}T12:00:00Z`,
    },
  });
  if (error) return { ok: false, error: error.message };
  const d = data as { ok?: boolean; error?: string; slots?: RawSlot[] } | null;
  if (!d?.ok) return { ok: false, error: d?.error ?? "availability_check_failed" };
  return buildDaySlots(d.slots, input.tzOffsetMinutes ?? 120);
}

// ─── Live availability (Slice 3c) — thin confirm-time wrapper ──────────────
// Answers "is this exact start free (for the optional therapist)?" by delegating
// to fetchChideskDaySlots. Kept for back-compat; the faceted panel no longer
// uses it for browsing. On any transport error we return { ok:false } so callers
// fail OPEN — a flaky availableTimes endpoint must never block bookings.
export type ChideskSlotCheck =
  // available: true = free, false = taken, null = couldn't determine (fail open)
  | { ok: true; available: boolean | null; slots: string[] }
  | { ok: false; error: string };

export async function checkChideskSlot(input: {
  tenantIntegrationId: string;
  treatmentGuid: string;
  staffGuid: string | null;
  startsAt: string;
  tzOffsetMinutes?: number;
}): Promise<ChideskSlotCheck> {
  const day = /^(\d{4}-\d{2}-\d{2})/.exec(input.startsAt)?.[1] ?? input.startsAt.slice(0, 10);
  // Query the EXACT therapist we're verifying. Under the union model a slot may be
  // owned by a non-first therapist; the proxy resolves a null staffGuid to the
  // FIRST bookable therapist, so omitting it would check the wrong person and
  // false-negative every slot the first therapist happens to be busy for.
  const offset = input.tzOffsetMinutes ?? 120;
  const ds = await fetchChideskDaySlots({
    tenantIntegrationId: input.tenantIntegrationId,
    treatmentGuid: input.treatmentGuid,
    day,
    staffGuid: input.staffGuid,
    tzOffsetMinutes: offset,
  });
  if (!ds.ok) return { ok: false, error: ds.error };
  // startsAt is the slot's UTC string; byMinute is keyed in TENANT-LOCAL minutes.
  const target = normMinute(wallMinutesOf(input.startsAt) + offset);
  const slot = ds.byMinute.get(target);
  const staffOk = !input.staffGuid || (slot ? slot.employeeIds.includes(input.staffGuid) : false);
  return {
    ok: true,
    available: slot != null && staffOk,
    slots: ds.slots.map((s) => s.start),
  };
}

// ─── Deposit payment intent ───────────────────────────────────────────
// Creates a payments row + reference. Caller hands the reference to
// initializePaystackCheckout and redirects. When `no_payment_required`
// is true the caller skips Paystack entirely and goes straight to
// bookChideskCart without a paymentReference.

export type ChideskDepositIntent =
  | {
      ok: true;
      no_payment_required: boolean;
      payment_id?: string;
      reference?: string;
      amount_cents: number;
      currency: string;
    }
  | { ok: false; reason: string };

export async function createChideskDepositPaymentIntent(input: {
  tenantIntegrationId: string;
  items: Array<{ treatment_row_id: string; qty?: number }>;
  customerEmail: string;
  customerPhone?: string;
  cartToken: string;
}): Promise<ChideskDepositIntent> {
  const { data, error } = await supabase.rpc(
    "create_chidesk_deposit_payment_intent",
    {
      p_tenant_integration_id: input.tenantIntegrationId,
      p_items: input.items,
      p_customer_email: input.customerEmail,
      p_customer_phone: input.customerPhone ?? null,
      p_cart_token: input.cartToken,
    },
  );
  if (error) return { ok: false, reason: error.message };
  return data as ChideskDepositIntent;
}

/**
 * Hand the server the booking payload before redirecting to Paystack, so a
 * settled deposit books even if this browser never comes back.
 *
 * The localStorage stash below is what the guest's own tab replays on return.
 * It is the ONLY record of what was owed — close the tab and the money is in
 * with no appointment and nothing saying which appointment it was for. The
 * same payload on the payments row lets paystack-webhook's finalizeMixedSpa
 * book it server-side, the rail staff pay-links have used since 20260709000050.
 *
 * Best-effort by design: if this fails the deposit still works exactly as it
 * did before, because finalize_status stays null and the browser keeps
 * ownership. It must never block the redirect to a page that takes money.
 */
export async function attachWidgetSpaPayload(input: {
  reference: string;
  cartToken: string;
  tenantIntegrationId: string;
  items: ChideskBookItem[];
  primary: ChideskGuestContact;
}): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc("attach_widget_spa_payload", {
      p_reference: input.reference,
      p_cart_token: input.cartToken,
      p_integration_id: input.tenantIntegrationId,
      p_book_items: input.items,
      p_primary: input.primary,
    });
    if (error) return false;
    return Boolean((data as { ok?: boolean } | null)?.ok);
  } catch {
    return false;
  }
}

// ─── Unified mixed-cart intent (native items + ChiDesk spa) ──────────────
// One payment covering the native cart total + the ChiDesk deposit. The spa
// booking payload is persisted server-side, so the webhook books it after
// payment (no localStorage, no client-side book on return). Returns a Paystack
// reference the widget hands to initializePaystackCheckout, same as native.

export type MixedPaymentIntent =
  | {
      ok: true;
      payment_id: string;
      reference: string;
      amount_cents: number;
      native_cents: number;
      chidesk_deposit_cents: number;
      currency: string;
    }
  | {
      ok: false;
      reason: string;
      /** reason='cohost_required' (event co-hosting gate): the required
       *  service's name, for the human error message. */
      service_name?: string;
    };

export async function createMixedPaymentIntent(input: {
  cartToken: string;
  guest: { name: string; email: string; phone: string; notes?: string | null };
  chideskIntegrationId: string;
  chideskItems: ChideskBookItem[];
  chideskDepositItems: Array<{ treatment_row_id: string; qty?: number }>;
  chideskPrimary: ChideskGuestContact;
}): Promise<MixedPaymentIntent> {
  const { data, error } = await supabase.rpc("create_mixed_payment_intent", {
    p_cart_token: input.cartToken,
    p_guest_name: input.guest.name,
    p_guest_email: input.guest.email,
    p_guest_phone: input.guest.phone,
    p_notes: input.guest.notes ?? null,
    p_chidesk_integration_id: input.chideskIntegrationId,
    p_chidesk_items: input.chideskItems,
    p_chidesk_deposit_items: input.chideskDepositItems,
    p_chidesk_primary: input.chideskPrimary,
  });
  if (error) return { ok: false, reason: error.message };
  return data as MixedPaymentIntent;
}

// ─── Status polling ────────────────────────────────────────────────────
// Anon-callable RPC. Used after the customer returns from Paystack to
// confirm the deposit settled before we call bookChideskCart.

export type ChideskPaymentStatus =
  | {
      ok: true;
      status: "pending" | "success" | "failed" | string;
      purpose: string;
      amount_cents: number;
      currency: string;
      cart_token: string | null;
      /**
       * Who finalises this deposit.
       *
       * null  — no booking payload on the server, so this browser owns it:
       *         the original localStorage flow, unchanged.
       * other — the server owns it (attachWidgetSpaPayload ran before the
       *         redirect). Wait for a terminal value instead of booking; the
       *         proxy's replay guard is a check-then-act, so two bookers
       *         arriving together would both read zero appointments and both
       *         book.
       */
      finalize_status?: string | null;
    }
  | { ok: false; reason: string };

export async function getChideskPaymentStatus(
  reference: string,
): Promise<ChideskPaymentStatus> {
  const { data, error } = await supabase.rpc(
    "get_payment_status_for_reference",
    { p_reference: reference },
  );
  if (error) return { ok: false, reason: error.message };
  return data as ChideskPaymentStatus;
}

// ─── Resume cart persistence ──────────────────────────────────────────
// The widget redirects to Paystack and comes back to the same URL with
// `?vt_chidesk_ref=<ref>`. Booking finalisation needs the original cart
// payload, so we stash it under a per-reference key in localStorage
// before redirecting and read it back on return.

const PENDING_KEY_PREFIX = "vt-chidesk-pending:";
const PENDING_TTL_MS = 1000 * 60 * 60 * 2; // 2h — paystack session well within

export interface PendingChideskBooking {
  tenantIntegrationId: string;
  cartToken: string;
  primary: ChideskGuestContact;
  items: ChideskBookItem[];
  /**
   * Each item's cart quantity (the party it is for), in items' order: an
   * item's amount_cents is the price for one, so what the treatments come to
   * is amount_cents × quantity, as the checkout counts it. Kept in this tab
   * only (never sent); absent on a booking saved before it existed (1 each).
   */
  quantities?: number[];
  reference: string;
  savedAt: number;
}

export function savePendingChideskBooking(payload: PendingChideskBooking): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      PENDING_KEY_PREFIX + payload.reference,
      JSON.stringify(payload),
    );
  } catch {
    /* quota or private mode — caller will surface a friendlier error if it matters */
  }
}

export function loadPendingChideskBooking(
  reference: string,
): PendingChideskBooking | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PENDING_KEY_PREFIX + reference);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingChideskBooking;
    if (
      !parsed?.savedAt ||
      Date.now() - parsed.savedAt > PENDING_TTL_MS
    ) {
      window.localStorage.removeItem(PENDING_KEY_PREFIX + reference);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingChideskBooking(reference: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PENDING_KEY_PREFIX + reference);
  } catch {
    /* ignore */
  }
}
